<template>
  <div>
    <LoadingOverlay v-if="isCreatingJobOffer" />

    <ClientOnly>
      <JobWizard is-new-job @finish="createJobOffer" />
    </ClientOnly>
  </div>
</template>

<script setup lang="ts">
import { GqlClientName } from '@ramp106/gql/clientNames'
import { jobsHrPaths } from '@ramp106/omrjs-routing'
import { ref, computed } from 'vue'

import JobWizard from '@/components/CreateJob/JobWizard.vue'
import LoadingOverlay from '@/components/LoadingOverlay.vue'
import { useLoggedInUser } from '@/composables/useUser'
import { JOB_FEATURE, JOB_OFFER_LEVEL, JOB_OFFER_TYPE } from '@/helpers/constants'
import { onJobSaveError } from '@/helpers/errors'

import { useI18n, useRoute, useRouter, navigateTo } from '#imports'
import { useCreateJobMutation } from '~/gql/jobs/requests.generated'
import { useNewJobStore } from '~/store/newJob'

useLoggedInUser()

const { locale } = useI18n()
const store = useNewJobStore()
const route = useRoute()
const router = useRouter()

const { mutate } = useCreateJobMutation({ clientId: GqlClientName.Jobs })

const isCreatingJobOffer = ref(false)

const jobOffer = computed(() => store.jobOffer)

const createJobOffer = async () => {
  isCreatingJobOffer.value = true

  try {
    try {
      const createJobOfferResult = await mutate({
        attributes: jobOffer.value,
      })
      const data = createJobOfferResult?.data?.createJob

      const id = data?.job?.id

      if (id) {
        store.resetDirtyFlag()
        store.resetJobOffer()

        navigateTo(jobsHrPaths.jobCreated({ locale: locale.value, slug: id }))
      } else {
        onJobSaveError(data?.errors)
      }
    } catch (error: unknown) {
      onJobSaveError([error instanceof Error ? error.message : String(error)])
    }
  } finally {
    isCreatingJobOffer.value = false
  }
}

const initJobOffer = () => {
  const { offerLevel = JOB_OFFER_LEVEL.BASIC, offerType = JOB_OFFER_TYPE.STANDARD } = route.query

  store.updateJobOffer({
    offerType: offerType as string,
  })

  if (offerLevel === JOB_OFFER_LEVEL.PREMIUM) {
    store.updateJobOffer({
      features: [
        JOB_FEATURE.TOP_JOB,
        JOB_FEATURE.SOCIAL_MEDIA_PUSH,
        JOB_FEATURE.REFRESH_HALFTIME,
        JOB_FEATURE.REFRESH_30,
        JOB_FEATURE.HIGHLIGHTING,
      ],
    })

    return
  }

  if (offerLevel === JOB_OFFER_LEVEL.BASIC) {
    store.updateJobOffer({
      features: [JOB_FEATURE.SOCIAL_MEDIA_PUSH, JOB_FEATURE.REFRESH_HALFTIME],
    })

    return
  }

  store.updateJobOffer({
    features: [],
  })
}

if (Object.keys(route.query).length !== 0) {
  router.replace({
    query: {},
  })
}

initJobOffer()
</script>
